import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function InvitationAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setErr("This invitation link is missing its code."); return; }
    if (!user) return; // wait for login redirect below
    api.post("/invitations/preview", { token })
      .then(({ data }) => setPreview(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "This invitation is invalid or expired."));
  }, [token, user]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invitations/accept", { token });
      toast.success("You've joined the business.");
      navigate(`/grow/${data.business_id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Could not accept the invitation.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-donkey-bg grain flex items-center justify-center px-5 py-16 text-donkey-warm">
      <div className="w-full max-w-md card-donkey rounded-2xl p-8 text-center animate-fade-up" data-testid="invite-accept">
        <DonkeyMascot size={100} alt="" className="mx-auto mb-4 opacity-95 drop-shadow-[0_0_40px_rgba(249,115,22,0.22)]" />
        <h1 className="font-heading font-bold text-2xl mb-2">Team invitation</h1>
        {!user && (
          <>
            <p className="text-sm text-donkey-grey mb-5">Please sign in or create an account to review this invitation.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate(`/login?next=/join?token=${encodeURIComponent(token || "")}`)} className="bg-donkey-orange text-donkey-black" data-testid="invite-login">Log in</Button>
              <Link to={`/register?next=/join?token=${encodeURIComponent(token || "")}`} className="text-donkey-orange underline self-center text-sm">Create account</Link>
            </div>
          </>
        )}
        {user && err && <p className="text-sm text-donkey-red" data-testid="invite-error">{err}</p>}
        {user && !err && !preview && <Loader2 className="w-6 h-6 animate-spin mx-auto text-donkey-orange" />}
        {user && preview && (
          <>
            <p className="text-sm text-donkey-grey mb-1">You've been invited to</p>
            <p className="font-heading font-semibold text-xl" data-testid="invite-business">{preview.business_name}</p>
            <p className="text-sm text-donkey-grey mt-1">as <span className="text-donkey-orange font-medium" data-testid="invite-role">{preview.role}</span>, by {preview.inviter_email}</p>
            {preview.already_member ? (
              <p className="text-sm text-donkey-amber mt-5">You are already a member of this business.</p>
            ) : (
              <Button onClick={accept} disabled={busy} className="mt-6 w-full bg-donkey-orange text-donkey-black font-semibold h-11" data-testid="invite-accept-btn">
                {busy ? "Joining…" : "Accept & join"}
              </Button>
            )}
            <p className="text-xs text-donkey-grey mt-4" data-testid="invite-inheritance-note">You'll use this business owner's plan — no separate subscription needed. Your access follows the owner's plan limits and your role.</p>
          </>
        )}
      </div>
    </div>
  );
}
